import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import TableMonth from 'components/FinancialReportLayout/TableMonth';
import CustomDatePicker from 'components/ui-kits/CustomDatePicker';
import styled from 'styled-components';
import { CalendarIcon } from 'components/ui-kits/Icon';
import DropdownFinancial from 'components/FinancialReportLayout/DropdownFinancial';
import { getFinancialReportV2 } from 'services/financialReportService';
import Loader from 'components/Loader';
import { Grid, GridItem } from 'components/ui-kits/Grid';
import type { Merchant, SubscriptionPlanDetail } from 'types/Merchant';
import { parseISO, startOfYear } from 'date-fns';
import ActionButton from '../ActionButton';
import { getDateFirstLastMonth } from 'utils';
import useSubscriptionPlan from 'hooks/useSubscriptionPlan';

const ByMonthStyle = styled.div`
  margin: 3rem 0;

  .custom-datepicker {
    width: 28rem;

    .react-datepicker-wrapper {
      display: block;
    }

    input {
      text-align: center;
    }
  }
`;

const BlockWrapper = styled.div`
  display: flex;
  width: 100%;
  & > div {
    margin-right: 2.8rem;
  }
`;

const ButtonWrapper = styled.span`
  margin-left: auto;
`;

interface ByMonthProps {
  setDisplayMode(mode: 'month' | 'period'): void;
  subscriptionPlans: SubscriptionPlanDetail[];
  merchant?: Merchant;
}

const ByMonth = ({
  setDisplayMode,
  subscriptionPlans,
  merchant
}: ByMonthProps) => {
  const { currentPlan, initialStartDate } =
    useSubscriptionPlan(subscriptionPlans);
  const [startDate, setStartDate] = useState(initialStartDate);

  const isAccountingReport = merchant?.isAccountingReport;

  const { data, isLoading } = useQuery(
    ['getByMonth', startDate.getFullYear()],
    ({ signal }) =>
      getFinancialReportV2({
        filter: 'by_month',
        startDate: getDateFirstLastMonth(
          new Date(startDate).getFullYear(),
          'first'
        ),
        endDate: getDateFirstLastMonth(
          new Date(startDate).getFullYear(),
          'last'
        ),
        signal
      })
  );

  const includeDates = useMemo(() => {
    if (isAccountingReport || subscriptionPlans.length === 0) {
      return undefined;
    }

    return subscriptionPlans.flatMap((plan) => {
      return [
        startOfYear(parseISO(plan.startDate)),
        startOfYear(parseISO(plan.endDate))
      ];
    });
  }, [isAccountingReport, subscriptionPlans]);

  return (
    <>
      {isLoading && <Loader />}
      <ByMonthStyle>
        <Grid col={11} start="large" marginRight={true}>
          <GridItem colStart={2} colEnd={10} nameStart="large" nameEnd="gutter">
            <BlockWrapper>
              <DropdownFinancial mode="month" setDisplayMode={setDisplayMode} />
              <CustomDatePicker
                selected={startDate}
                onChange={(date: Date) => setStartDate(date)}
                showYearPicker
                dateFormat="yyyy"
                variant="round"
                renderYearContent={(year: number) =>
                  renderYearContent(year, includeDates)
                }
                includeDates={includeDates}
                icon={<CalendarIcon />}
              />
              <ButtonWrapper>
                <ActionButton
                  planType="financial_report"
                  currentPlan={currentPlan}
                  isAccountingReport={isAccountingReport}
                />
              </ButtonWrapper>
            </BlockWrapper>
          </GridItem>
        </Grid>
        {data && (
          <TableMonth
            dataByMonth={data}
            year={new Date(startDate).getFullYear()}
            key={new Date(startDate).getFullYear()}
          />
        )}
      </ByMonthStyle>
    </>
  );
};

function renderYearContent(year: number, includeDates?: Date[]) {
  if (!includeDates) {
    return year;
  }

  const isYearIncluded = includeDates.some(
    (date) => date.getFullYear() === year
  );

  return isYearIncluded ? year : <span style={{ opacity: 0.5 }}>{year}</span>;
}
export default ByMonth;
